import { useEffect, useState } from "react";
import { useLocation } from "wouter";

type ProblemOverview = {
  schema_version: string;
  normalized_problem_instances: number;
  active_problem_instances: number;
  provenance_verified_problem_instances: number;
  eligible_pair_candidates: number;
  cross_slice_pair_candidates: number;
  candidate_is_verified_correlation: false;
};

type BidirectionalOverview = {
  schema_version: string;
  correlations: number;
  bidirectionally_verified: number;
  accountability_notices: number;
};

const style = `
.pcurrent{min-height:calc(100vh - 70px);padding:clamp(20px,5vw,64px);background:linear-gradient(130deg,#101820,#070b11);color:#eaf4fa;font-family:inherit}
.pcurrent-inner{max-width:1180px;margin:0 auto}
.pcurrent .kicker{font-size:12px;letter-spacing:.15em;text-transform:uppercase;color:#70cfdf;font-weight:700}
.pcurrent h1{font-size:clamp(29px,4.5vw,48px);line-height:1.12;margin:15px 0;max-width:850px}
.pcurrent h2{font-size:20px;margin:5px 0 12px}
.pcurrent p{max-width:850px;color:#b7c9d2;line-height:1.7}
.pcurrent-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(225px,1fr));gap:14px;margin:27px 0}
.pcurrent-card{background:#121f29;border:1px solid #2b4855;border-radius:13px;padding:23px}
.pcurrent-card strong{display:block;font-size:34px;line-height:1.2;margin:13px 0}
.pcurrent-card small{color:#91adbd}
.pcurrent-panel{background:#101d27;border:1px solid #36505b;border-radius:14px;padding:24px;margin-top:18px}
.pcurrent-actions{display:flex;gap:12px;flex-wrap:wrap;margin-top:18px}
.pcurrent-actions button{color:#e6faff;background:#193646;border:1px solid #4d92a4;padding:12px 16px;border-radius:8px;font-weight:600;cursor:pointer}
.pcurrent-actions button:focus-visible{outline:3px solid #8fe6f7}
.pcurrent .muted{color:#91adbd;font-size:13px}
`;

function Metric({ label, value, note }: { label: string; value: number | string; note: string }) {
  return <article className="pcurrent-card"><span className="kicker">{label}</span><strong>{value}</strong><small>{note}</small></article>;
}

export function PrismCurrentSourceGate() {
  const [, navigate] = useLocation();
  return <main className="pcurrent"><style>{style}</style><div className="pcurrent-inner">
    <span className="kicker">Source-generation integrity gate</span>
    <h1>Current Rosetta verification is not connected to Prism yet.</h1>
    <p>Prism has historical Rosetta structural-verification receipts, but they were produced against earlier decomposition generations. They must not be displayed as current findings, current contradictions, or current verification percentages. No current-generation proof has been inferred from those records.</p>
    <section className="pcurrent-panel"><h2>What needs to happen next</h2>
      <p>Rosetta must expose an explicitly versioned, source-hash-bound current-generation output contract. Prism must verify that contract and independently issue new receipts before displaying current source-check results. Historical records remain untouched and distinct.</p>
      <p className="muted">State: CURRENT_SOURCE_CONTRACT_NOT_CONNECTED. This is unavailable evidence, not a verified zero.</p>
      <div className="pcurrent-actions"><button onClick={() => navigate("/")}>Prism operational overview</button><button onClick={() => navigate("/bidirectional-core")}>Bidirectional Core</button><button onClick={() => navigate("/correlation-map")}>Correlation Map</button></div>
    </section>
  </div></main>;
}

export default function PrismCurrentDashboard() {
  const [, navigate] = useLocation();
  const [problem, setProblem] = useState<ProblemOverview | null>(null);
  const [bidirectional, setBidirectional] = useState<BidirectionalOverview | null>(null);
  const [problemError, setProblemError] = useState<string | null>(null);
  const [bidirectionalError, setBidirectionalError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const read = async (url: string) => {
      const response = await fetch(url, { cache: "no-store" });
      if (!response.ok) throw new Error(`http_${response.status}`);
      return response.json();
    };
    read("/api/v1/problems/overview").then((value: ProblemOverview) => {
      if (!live) return;
      if (value.candidate_is_verified_correlation !== false || value.schema_version !== "prism-problem-intake-1.0.0") throw new Error("invalid_problem_contract");
      setProblem(value);
    }).catch((error) => { if (live) setProblemError(error instanceof Error ? error.message : "unavailable"); });
    read("/api/v1/bidirectional/overview").then((value: BidirectionalOverview) => {
      if (!live) return;
      if (value.schema_version !== "prism-bidirectional-1.0.0") throw new Error("invalid_bidirectional_contract");
      setBidirectional(value);
    }).catch((error) => { if (live) setBidirectionalError(error instanceof Error ? error.message : "unavailable"); });
    return () => { live = false; };
  }, []);

  return <main className="pcurrent"><style>{style}</style><div className="pcurrent-inner">
    <span className="kicker">Prism · operational inventory</span>
    <h1>Inspect the evidence boundary before trusting a result.</h1>
    <p>This overview shows what Prism has stored, not what has been independently admitted as current evidence. The recorded problem and pair populations were created in August 2026; their current upstream eligibility has not been reconciled. Candidate pairs are not verified correlations, and historical Rosetta receipts are excluded.</p>
    <div className="pcurrent-grid">
      <Metric label="Stored problem inventory" value={problem?.normalized_problem_instances ?? (problemError ? "Unavailable" : "Loading")} note="Unreconciled against current source contracts" />
      <Metric label="Stored provenance flags" value={problem?.provenance_verified_problem_instances ?? (problemError ? "Unavailable" : "Loading")} note="Database-declared; upstream source eligibility not independently established" />
      <Metric label="Stored candidate pairs" value={problem?.eligible_pair_candidates ?? (problemError ? "Unavailable" : "Loading")} note="Historical candidate inventory; not current causal proof" />
      <Metric label="Persisted bidirectional proof" value={bidirectional?.bidirectionally_verified ?? (bidirectionalError ? "Unavailable" : "Loading")} note="Recorded verified correlations; does not imply current Rosetta source eligibility" />
    </div>
    {(problemError || bidirectionalError) ? <p className="muted">Operational reads incomplete: {problemError ? `problem intake: ${problemError}` : ""} {bidirectionalError ? `bidirectional: ${bidirectionalError}` : ""}. Unavailable values are never represented as zero.</p> : null}
    <section className="pcurrent-panel">
      <span className="kicker">Upstream source-integrity boundary</span>
      <h2>Current Rosetta verification: not connected</h2>
      <p>The previous command center displayed historical Rosetta structural-binding receipts as if they reflected current decomposition. That feed is retired from active Prism surfaces pending a verified current-generation producer and receipt contract. Its historical records have not been rewritten or counted here.</p>
      <div className="pcurrent-actions"><button onClick={() => navigate("/control-room")}>Source-verification status</button><button onClick={() => navigate("/correlation-map")}>Correlation Map</button><button onClick={() => navigate("/bidirectional-core")}>Bidirectional Core</button></div>
    </section>
  </div></main>;
}
